// Extraction/OCR des pièces jointes (job asynchrone + polling, voir pages/attachmentsPage.ts).
import { getPraetoriaApiToken, PRAETORIA_API_BASE_URL } from "../config";
import { buildApiError, PraetoriaTimeoutError } from "./errors";
import { AttachmentInput, ExtractedAttachment } from "./types";

function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
}

interface AttachmentExtractionStatus {
  status: "pending" | "processing" | "done" | "error";
  processed?: number;
  total?: number;
  attachments?: ExtractedAttachment[];
  detail?: string;
}

export interface AttachmentExtractionProgress {
  processed: number;
  total: number;
}

const ATTACHMENTS_POLL_INTERVAL_MS = 5000;
const ATTACHMENTS_POLL_REQUEST_TIMEOUT_MS = 15000;

async function submitAttachmentsExtraction(
  attachments: AttachmentInput[],
  graphToken: string,
  token: string
): Promise<string> {
  const formData = new FormData();
  for (const attachment of attachments) {
    formData.append("files", base64ToBlob(attachment.contentBytes, attachment.contentType), attachment.name);
  }
  formData.append("graph_access_token", graphToken);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), ATTACHMENTS_POLL_REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${PRAETORIA_API_BASE_URL}/app/email/attachments/extract/submit`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new PraetoriaTimeoutError();
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw await buildApiError(response);
  }

  const data = await response.json();
  return data.job_id;
}

async function getAttachmentsExtractionStatus(jobId: string, token: string): Promise<AttachmentExtractionStatus> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), ATTACHMENTS_POLL_REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(
      `${PRAETORIA_API_BASE_URL}/app/email/attachments/extract/status/${encodeURIComponent(jobId)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      }
    );
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      // Cette requête de poll a juste été lente à répondre -- distinct d'une vraie erreur réseau,
      // l'appelant retente sans compter ça comme un échec.
      throw new PraetoriaTimeoutError();
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw await buildApiError(response);
  }

  return response.json();
}

export async function extractAttachmentsText(
  attachments: AttachmentInput[],
  graphToken: string,
  onProgress?: (progress: AttachmentExtractionProgress) => void
): Promise<ExtractedAttachment[]> {
  const token = getPraetoriaApiToken();
  if (!token) {
    throw new Error("Token API Praetoria manquant (PRAETORIA_API_TOKEN).");
  }

  const jobId = await submitAttachmentsExtraction(attachments, graphToken, token);

  // Pas de budget de temps total : le serveur ne purge pas les jobs en cours, donc on poll tant
  // que le panneau reste ouvert, jusqu'à un statut final ou une vraie erreur (pas un simple délai
  // d'attente sur une requête de poll, qui est retenté indéfiniment).
  while (true) {
    let status: AttachmentExtractionStatus;
    try {
      status = await getAttachmentsExtractionStatus(jobId, token);
    } catch (error) {
      if (error instanceof PraetoriaTimeoutError) {
        await new Promise((resolve) => setTimeout(resolve, ATTACHMENTS_POLL_INTERVAL_MS));
        continue;
      }
      throw error;
    }

    if (status.status === "done") {
      return status.attachments ?? [];
    }
    if (status.status === "error") {
      throw new Error(status.detail ?? "Échec de la lecture des pièces jointes.");
    }

    onProgress?.({ processed: status.processed ?? 0, total: status.total ?? attachments.length });
    await new Promise((resolve) => setTimeout(resolve, ATTACHMENTS_POLL_INTERVAL_MS));
  }
}
